import type { FC } from "react";
import { useLayoutEffect } from "react";
import { Stack, Box, Typography, Button } from "@mui/material";
import { visuallyHidden } from "@mui/utils";
import { ArrowLeftIcon, ArrowRightIcon } from "../../shared/icons";
import { usePrivacyNavigate } from "../../shared/usePrivacyNavigate";
import { DynamicInput } from "../DynamicInput";
import { QuestionCardContent } from "./QuestionCardContent";
import { MULTI_STEP_FORM_SCROLL_CONTAINER } from "./MultiStepformDialog";
import { TraverseQuestionCardComponentBasedOnRule } from "../shared/TraverseQuestionCard";
import type { Section } from "../types";

type MultiStepSectionsProps = { sections: Section[]; currentSection: Section };
export const Sections: FC<MultiStepSectionsProps> = ({ sections, currentSection }) => {
  const { navigate } = usePrivacyNavigate();

  /** react-router-dom helps with Scroll Restoration etc. restores the page scroll
   * position when returns to a page with the back button. We probably don't want this
   * for the form. Instead, we want to always see the top of the section.  **/
  useLayoutEffect(() => {
    document.getElementById(MULTI_STEP_FORM_SCROLL_CONTAINER)?.scrollTo(0, 0);
  }, [currentSection.id]);

  const currentSectionIndex = sections.findIndex(({ id }) => currentSection.id === id);
  const nextSection = sections.at(currentSectionIndex + 1);
  const previousSection = currentSectionIndex > 0 ? sections[currentSectionIndex - 1] : undefined;

  const goToNext = () => nextSection?.id && navigate(`../${nextSection.id}`);

  const goToPrevious = () => previousSection?.id && navigate(`../${previousSection.id}`);

  return (
    <Stack sx={{ py: 3, width: "100%" }}>
      <Typography component="h2" sx={visuallyHidden}>
        {currentSection.name}
      </Typography>
      {currentSection.questionCards.map(rootQuestionCard => (
        <TraverseQuestionCardComponentBasedOnRule
          key={rootQuestionCard.id}
          rootQuestionCard={rootQuestionCard}
          render={questionCard => (
            <QuestionCardContent questionCard={questionCard}>
              <DynamicInput variant="multiStep" questionCard={questionCard} />
            </QuestionCardContent>
          )}
        />
      ))}
      <Stack direction="row" sx={{ justifyContent: "space-between", mt: 2 }}>
        <ArrowSectionButton direction="left" sectionName={previousSection?.name} onClick={goToPrevious} />
        <ArrowSectionButton direction="right" sectionName={nextSection?.name} onClick={goToNext} />
      </Stack>
    </Stack>
  );
};

type ArrowSectionButtonProps = {
  onClick: () => void;
  direction: "left" | "right";
  sectionName?: string;
};
export const ArrowSectionButton: FC<ArrowSectionButtonProps> = ({ onClick, direction, sectionName }) => {
  /** Makes it easier for layout if always element is in the DOM since
   * we can use justify-content=space-between on the parent */
  if (!sectionName) return <Box />;

  return (
    <Box sx={{ minWidth: 200 }}>
      <Button
        size="large"
        variant="outlined"
        onClick={onClick}
        fullWidth
        {...(direction === "left"
          ? { startIcon: <ArrowLeftIcon size={22} /> }
          : { endIcon: <ArrowRightIcon size={22} /> })}
      >
        {sectionName}
      </Button>
    </Box>
  );
};
